/**
 * integrations-shared.js — everything the Integrations hub and the per-provider
 * detail pages share: descriptors, walkthrough copy, status derivation, the
 * injected stylesheet, and every action (connect / disconnect / sync / the
 * sub-account pickers / the CallRail paste-key flow).
 *
 * Why one module: Google Ads and Google Analytics are the same integration in
 * two costumes, and the 0dcf1f4 drift bug (an error-surfacing fix landing on
 * one copy and missing the other) is what the descriptor design exists to
 * prevent. The hub renders navigation tiles only; ALL configuration lives on
 * the detail pages, driven by these descriptors.
 *
 * Actions take an optional `refresh` callback (the calling page's reload)
 * instead of assuming the hub's load() - the same handler works on either page.
 */

import AuthController from './AuthController.js';
import PubSub from 'pubsub-js';
import AppBroadcast from '../../../../shared/appBroadcast.js';
import { confirmDialog } from '../core/confirmDialog.js';
import { guardedFetch, onNavigationDiscard } from '../core/navGeneration.js';

export const ESC = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
export const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ESC[c]);
export const toast = (message, type = 'info') => { try { PubSub.publish(AppBroadcast.UI_TOAST, { message, type }); } catch { /* no-op */ } };
export const apiBase = () => AuthController.getConfig?.().BASE_URL || '';
export const orgGuid = () => AuthController.getActiveOrg()?.guid || AuthController.getMemberships()[0]?.orgGuid || null;
export const canManage = () => AuthController.getCapabilities().includes('manage_magnets');

export const emptyState = (icon, msg) =>
  `<div class="m5t-empty"><span class="material-symbols-outlined" aria-hidden="true">${icon}</span><p>${msg}</p></div>`;

export const alertHTML = (msg, kind = '') =>
  `<div class="intg-alert ${kind}"><span class="material-symbols-outlined" aria-hidden="true">${kind === 'error' ? 'error' : 'info'}</span><span>${msg}</span></div>`;

// Shared busy latch across both pages (a click during an in-flight action is a no-op).
const busyLock = { busy: false };

// Release the latch when the user navigates away. LOAD-BEARING for navGeneration, not
// cleanup: guardedFetch never settles for an abandoned page, so the `finally` blocks
// below that clear this latch never run. This module is an ES singleton shared by seven
// route modules, so a stranded `busy: true` would silently disable every integration
// action app-wide until a full reload. Today the latch clears because the fetch settles;
// without this line the guard would INTRODUCE that failure rather than inherit it.
onNavigationDiscard(() => { busyLock.busy = false; });

// ── OAuth-return handoff (hub -> detail) ─────────────────────────────────────
// The server callback always lands on the hub (its redirect target is part of
// four OAuth app registrations - not worth changing). The hub toasts, strips
// the search string, parks the outcome here, and navigates to the provider's
// detail page, which picks it up to auto-open the right picker.
let pendingReturn = null;
export function setPendingReturn(v) { pendingReturn = v; }
export function takePendingReturn() { const v = pendingReturn; pendingReturn = null; return v; }

// ── walkthrough copy ─────────────────────────────────────────────────────────
// One sentence of "what this does" + numbered setup steps per provider. On the
// detail page these render as a real stepper with done-states; once healthy
// they collapse into a "Setup guide" disclosure.
import {
  WALKTHROUGHS, PROVIDERS, CATEGORIES, AUTOMATION_TILES, AUTOMATION_INTEGRATIONS, WIDE_MARKS,
} from './integrations-registry.js';

// Re-exported so every existing import site keeps working; the data itself
// lives in integrations-registry.js - the single source of truth that also
// generates the website /integrations/ page and the docs pages
// (scripts/export-integrations.mjs). New integration = one registry entry.
export { WALKTHROUGHS, PROVIDERS, CATEGORIES, AUTOMATION_TILES, AUTOMATION_INTEGRATIONS };

export const URL_TO_SLUG = Object.fromEntries(Object.values(PROVIDERS).map((p) => [p.urlSlug, p.slug]));

// ── status + shared render pieces ────────────────────────────────────────────
/** The provider mark: real brand SVG inside the tinted tile, Material glyph as
 * the onerror fallback (CallRail ships this way until its press-kit SVG lands). */
export function logoHTML(p, { size = '' } = {}) {
  return `
    <span class="intg-mark${size ? ` intg-mark--${size}` : ''}${WIDE_MARKS.has(p.slug) ? ' intg-mark--wide' : ''}">
      <img class="intg-logo" src="${esc(p.logo)}" alt="" loading="lazy"
           onerror="this.style.display='none';this.parentElement.querySelector('.intg-logo-fallback').style.display=''">
      <span class="material-symbols-outlined intg-logo-fallback" aria-hidden="true" style="display:none">${p.card.icon}</span>
    </span>`;
}

export function connFor(providersMap, slug) {
  const org = orgGuid();
  const conns = providersMap?.[slug] || [];
  return conns.find((c) => c.owner_guid === org) || conns[0] || null;
}

/** Uniform status derivation: unavailable | idle | connected | broken. */
export function statusOf(p, conn, configured) {
  // CallRail has no platform config gate (configured is always true server-side).
  if (p.connect && !configured) return 'unavailable';
  if (!conn) return 'idle';
  return conn.connect_status === 'ACTIVE' ? 'connected' : 'broken';
}

export function statusPill(status, conn) {
  if (status === 'unavailable') return `<span class="intg-state">Not available yet</span>`;
  if (status === 'idle') return `<span class="intg-state">Not connected</span>`;
  if (status === 'connected') return `<span class="intg-state on"><span class="material-symbols-outlined" aria-hidden="true">check</span>Connected</span>`;
  return `<span class="intg-state warn"><span class="material-symbols-outlined" aria-hidden="true">priority_high</span>${esc(conn?.connect_status === 'NEEDS_REAUTH' ? 'Reconnect needed' : 'Connection error')}</span>`;
}

/** GET /integrations for the active org. Returns {denied:true} on 403. */
export async function fetchIntegrations() {
  const res = await guardedFetch(`${apiBase()}/m5t/v5/integrations`);
  if (res.status === 403) return { denied: true };
  const data = await res.json();
  if (!data.ok) throw new Error(data.error || 'load failed');
  return { providers: data.data.providers, configured: data.data.configured };
}

// ── actions (shared by hub + detail; `refresh` is the caller's reload) ───────
export async function connectProvider(p) {
  if (busyLock.busy) return;
  busyLock.busy = true;
  try {
    const res = await guardedFetch(`${apiBase()}/m5t/v5/integrations/${p.slug}/connect`);
    const data = await res.json();
    if (!data.ok || !data.data?.url) throw new Error(data.error || 'connect failed');
    window.location.href = data.data.url;
  } catch (e) {
    // Surface the REAL reason (e.g. "Google Ads is not configured on this
    // server") - the whole point of the shared body; see 0dcf1f4.
    toast(e.message || p.connect.fallbackError, 'error');
    busyLock.busy = false;
  }
}

export async function syncProvider(p, { refresh } = {}) {
  if (busyLock.busy) return;
  busyLock.busy = true;
  const btn = document.getElementById(p.card.ids.sync);
  if (btn) btn.disabled = true;
  try {
    const res = await guardedFetch(`${apiBase()}/m5t/v5/integrations/${p.slug}/sync`, { method: 'POST' });
    const data = await res.json();
    if (!data.ok) throw new Error(data.error || 'sync failed');
    const items = data.data?.connections?.[0]?.[p.sync.itemsField] || [];
    const failed = items.filter((it) => it.error).length;
    toast(failed ? p.sync.failedToast(failed) : 'Metrics synced.', failed ? 'warning' : 'success');
  } catch (e) {
    toast('Sync failed.', 'error');
  } finally {
    busyLock.busy = false;
    if (btn) btn.disabled = false;
    refresh?.();
  }
}

export async function disconnectProvider(p, { refresh } = {}) {
  if (busyLock.busy) return;
  // Latch BEFORE awaiting the dialog: a second activation while it is open
  // must not stack a second confirm (and double-DELETE on both OKs).
  busyLock.busy = true;
  let ok = false;
  try {
    ok = await confirmDialog({
      title: `Disconnect ${p.label}?`,
      message: p.disconnect.confirm,
      confirmLabel: 'Disconnect',
      danger: true,
    });
  } finally {
    if (!ok) busyLock.busy = false;
  }
  if (!ok) return;
  try {
    const res = await guardedFetch(`${apiBase()}/m5t/v5/integrations/${p.slug}`, { method: 'DELETE' });
    const data = await res.json().catch(() => ({}));
    if (!data.ok) throw new Error(data.error || 'disconnect failed');
    toast(p.disconnect.toast, 'success');
  } catch (e) {
    toast('Disconnect failed.', 'error');
  } finally {
    busyLock.busy = false;
    refresh?.();
  }
}

// ── sub-account picker (Google Ads accounts / GA4 properties) ────────────────
export async function openProviderPicker(p, { refresh } = {}) {
  const pk = p.picker;
  const panel = document.getElementById(p.card.ids.panel);
  if (!panel) return;
  panel.style.display = '';
  panel.innerHTML = alertHTML(pk.loading);
  try {
    const res = await guardedFetch(`${apiBase()}/m5t/v5/integrations/${p.slug}/${pk.path}`);
    const data = await res.json();
    if (!data.ok) throw new Error(data.error || 'load failed');
    renderProviderPicker(p, data.data[pk.itemsField] || [], new Set(data.data.linked || []), { refresh });
  } catch (e) {
    panel.innerHTML = alertHTML(pk.loadError, 'error');
  }
}

function renderProviderPicker(p, items, linked, { refresh } = {}) {
  const pk = p.picker;
  const panel = document.getElementById(p.card.ids.panel);
  if (!panel) return;

  const rows = items.map((it) => {
    const disabled = pk.rowDisabled(it);
    const badge = pk.rowBadge(it);
    const checked = linked.has(it.id) && !disabled;
    return `
      <label class="intg-acct${disabled ? ' disabled' : ''}">
        <input type="checkbox" data-${pk.dataAttr}="${esc(it.id)}" ${checked ? 'checked' : ''} ${disabled ? 'disabled' : ''}>
        <span style="min-width:0">
          <span class="intg-acct__name">${esc(it.name || pk.namePrefix + it.id)}</span>
          <div class="intg-acct__id">${esc(it.id)}</div>
        </span>
        ${badge ? `<span class="intg-acct__badge">${esc(badge)}</span>` : '<span></span>'}
      </label>`;
  }).join('');

  panel.innerHTML = `
    <div class="intg-picker">
      <div class="intg-picker__head">
        <span>${pk.heading}</span>
        <span style="font-weight:500">${items.filter((it) => !pk.rowDisabled(it)).length} selectable</span>
      </div>
      <div class="intg-picker__list">
        ${rows || `<div class="intg-picker__empty">${pk.emptyMsg}</div>`}
      </div>
      <div class="intg-picker__foot">
        <button type="button" class="intg-btn" id="${pk.ids.cancel}">Cancel</button>
        <button type="button" class="intg-btn primary" id="${pk.ids.save}"><span class="material-symbols-outlined" aria-hidden="true">save</span>Save</button>
      </div>
    </div>`;

  document.getElementById(pk.ids.cancel)?.addEventListener('click', () => { panel.style.display = 'none'; });
  document.getElementById(pk.ids.save)?.addEventListener('click', () => saveProviderPicker(p, { refresh }));
}

async function saveProviderPicker(p, { refresh } = {}) {
  const pk = p.picker;
  const panel = document.getElementById(p.card.ids.panel);
  const ids = [...(panel?.querySelectorAll(`input[data-${pk.dataAttr}]:checked`) || [])].map((el) => el.dataset[pk.datasetKey]);
  const btn = document.getElementById(pk.ids.save);
  if (btn) btn.disabled = true;
  try {
    const res = await guardedFetch(`${apiBase()}/m5t/v5/integrations/${p.slug}/${pk.path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ [pk.saveField]: ids }),
    });
    const data = await res.json();
    if (!data.ok) throw new Error(data.error || 'save failed');
    toast(pk.savedToast(data.data.linked), 'success');
    if (panel) panel.style.display = 'none';
    refresh?.();
  } catch (e) {
    toast(pk.saveError, 'error');
    if (btn) btn.disabled = false;
  }
}

// ── CallRail: paste-key setup (no OAuth exists for their API) ────────────────
// HighLevel pasted-token connect: token + sub-account Location ID.
export function openGhlSetup({ refresh } = {}) {
  const panel = document.getElementById(PROVIDERS.gohighlevel.card.ids.setup);
  if (!panel) return;
  panel.style.display = '';
  panel.innerHTML = `
    <div class="intg-picker">
      <div class="intg-picker__head"><span>Connect HighLevel</span></div>
      <div class="intg-form">
        <div>
          <label for="intg-gh-token">Private integration token</label>
          <input class="intg-input" id="intg-gh-token" type="password" autocomplete="off" spellcheck="false" placeholder="pit-...">
          <p class="intg-hint">Create one in the sub-account under Settings, then Private Integrations, with the contacts.write scope.</p>
        </div>
        <div>
          <label for="intg-gh-location">Location ID</label>
          <input class="intg-input" id="intg-gh-location" type="text" autocomplete="off" spellcheck="false" placeholder="The sub-account Location ID">
          <p class="intg-hint">Copy it from the sub-account under Settings, then Business Profile.</p>
        </div>
        <p class="intg-hint" id="intg-gh-status" role="status"></p>
      </div>
      <div class="intg-picker__foot">
        <button type="button" class="intg-btn" id="intg-gh-cancel">Cancel</button>
        <button type="button" class="intg-btn primary" id="intg-gh-save">Connect</button>
      </div>
    </div>`;
  const status = (msg) => { const el = document.getElementById('intg-gh-status'); if (el) el.textContent = msg; };
  document.getElementById('intg-gh-cancel')?.addEventListener('click', () => { panel.style.display = 'none'; });
  document.getElementById('intg-gh-save')?.addEventListener('click', async () => {
    const btn = document.getElementById('intg-gh-save');
    const token = document.getElementById('intg-gh-token')?.value.trim() || '';
    const locationId = document.getElementById('intg-gh-location')?.value.trim() || '';
    if (!token || !locationId) { status('Paste the token and the Location ID first.'); return; }
    btn.disabled = true;
    status('Checking with HighLevel…');
    try {
      const res = await guardedFetch(`${apiBase()}/m5t/v5/integrations/gohighlevel/connect`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, location_id: locationId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!data.ok) throw new Error(data.error || 'Could not connect HighLevel.');
      toast(`HighLevel connected${data.data.location_name ? ` to ${data.data.location_name}` : ''}.`, 'success');
      panel.style.display = 'none';
      refresh?.();
    } catch (e) {
      status(e.message || 'Could not connect HighLevel.');
      btn.disabled = false;
    }
  });
  document.getElementById('intg-gh-token')?.focus();
}

// Cal.com pasted-key connect: ONE field, one call - the server validates the
// key against /v2/me and auto-subscribes booking webhooks.
export function openCalcomSetup({ refresh } = {}) {
  const panel = document.getElementById(PROVIDERS.calcom.card.ids.setup);
  if (!panel) return;
  panel.style.display = '';
  panel.innerHTML = `
    <div class="intg-picker">
      <div class="intg-picker__head"><span>Connect Cal.com</span></div>
      <div class="intg-form">
        <div>
          <label for="intg-cc-key">API key</label>
          <input class="intg-input" id="intg-cc-key" type="password" autocomplete="off" spellcheck="false" placeholder="cal_live_...">
          <p class="intg-hint">Create one in Cal.com under Settings, then Developer, then API keys.</p>
        </div>
        <p class="intg-hint" id="intg-cc-status" role="status"></p>
      </div>
      <div class="intg-picker__foot">
        <button type="button" class="intg-btn" id="intg-cc-cancel">Cancel</button>
        <button type="button" class="intg-btn primary" id="intg-cc-save">Connect</button>
      </div>
    </div>`;
  const status = (msg) => { const el = document.getElementById('intg-cc-status'); if (el) el.textContent = msg; };
  document.getElementById('intg-cc-cancel')?.addEventListener('click', () => { panel.style.display = 'none'; });
  document.getElementById('intg-cc-save')?.addEventListener('click', async () => {
    const btn = document.getElementById('intg-cc-save');
    const key = document.getElementById('intg-cc-key')?.value.trim() || '';
    if (!key) { status('Paste an API key first.'); return; }
    btn.disabled = true;
    status('Checking the key with Cal.com…');
    try {
      const res = await guardedFetch(`${apiBase()}/m5t/v5/integrations/calcom/connect`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ api_key: key }),
      });
      const data = await res.json().catch(() => ({}));
      if (!data.ok) throw new Error(data.error || 'Could not connect Cal.com.');
      toast(`Cal.com connected as ${data.data.username || 'your account'}.`, 'success');
      panel.style.display = 'none';
      refresh?.();
    } catch (e) {
      status(e.message || 'Could not connect Cal.com.');
      btn.disabled = false;
    }
  });
  document.getElementById('intg-cc-key')?.focus();
}

export function openCallrailSetup({ refresh, replaceKey = false } = {}) {
  const panel = document.getElementById(PROVIDERS.callrail.card.ids.setup);
  if (!panel) return;
  panel.style.display = '';
  panel.innerHTML = `
    <div class="intg-picker">
      <div class="intg-picker__head"><span>${replaceKey ? 'Replace API key' : 'Connect CallRail'}</span></div>
      <div class="intg-form">
        <div>
          <label for="intg-r-key">API key</label>
          <input class="intg-input" id="intg-r-key" type="password" autocomplete="off" spellcheck="false" placeholder="Paste your CallRail API v3 key">
          <p class="intg-hint">Create one in CallRail under Integrations, then Data access, then API keys. Read-only is fine for calls; Allow Writes lets us register call sync for you.</p>
        </div>
        <div id="intg-r-pickers" hidden>
          <label for="intg-r-account">Account</label>
          <select class="intg-select" id="intg-r-account"></select>
          <label for="intg-r-company" style="margin-top:8px;display:block">Company (your client)</label>
          <select class="intg-select" id="intg-r-company"></select>
        </div>
        <p class="intg-hint" id="intg-r-status" role="status"></p>
      </div>
      <div class="intg-picker__foot">
        <button type="button" class="intg-btn" id="intg-r-cancel">Cancel</button>
        <button type="button" class="intg-btn primary" id="intg-r-continue">Continue</button>
      </div>
    </div>`;
  document.getElementById('intg-r-cancel')?.addEventListener('click', () => { panel.style.display = 'none'; });
  document.getElementById('intg-r-continue')?.addEventListener('click', () => callrailContinue({ refresh }));
  document.getElementById('intg-r-key')?.focus();
}

const rStatus = (msg) => { const el = document.getElementById('intg-r-status'); if (el) el.textContent = msg; };

async function callrailContinue({ refresh } = {}) {
  const btn = document.getElementById('intg-r-continue');
  const key = document.getElementById('intg-r-key')?.value.trim() || '';
  const pickers = document.getElementById('intg-r-pickers');
  if (!key) { rStatus('Paste an API key first.'); return; }

  // Second click (pickers visible) = finalize.
  if (pickers && !pickers.hidden) return callrailSave(key, { refresh });

  if (btn) btn.disabled = true;
  rStatus('Checking the key with CallRail…');
  try {
    const res = await guardedFetch(`${apiBase()}/m5t/v5/integrations/callrail/validate`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ api_key: key }),
    });
    const data = await res.json();
    if (!data.ok) { rStatus(data.error || 'CallRail rejected that key.'); return; }
    const accounts = data.data.accounts || [];
    const acctSel = document.getElementById('intg-r-account');
    if (acctSel) {
      acctSel.innerHTML = accounts.map((a) => `<option value="${esc(a.id)}">${esc(a.name)}</option>`).join('');
      acctSel.onchange = () => loadCallrailCompanies(key, acctSel.value);
    }
    if (pickers) pickers.hidden = false;
    if (btn) btn.innerHTML = `<span class="material-symbols-outlined" aria-hidden="true">save</span>Save`;
    rStatus('');
    await loadCallrailCompanies(key, accounts[0]?.id);
  } catch (e) {
    rStatus('Could not reach CallRail. Try again.');
  } finally {
    if (btn) btn.disabled = false;
  }
}

async function loadCallrailCompanies(key, accountId) {
  const sel = document.getElementById('intg-r-company');
  if (!sel || !accountId) return;
  sel.innerHTML = `<option value="">Loading companies…</option>`;
  try {
    const res = await guardedFetch(`${apiBase()}/m5t/v5/integrations/callrail/companies`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ api_key: key, account_id: accountId }),
    });
    const data = await res.json();
    const companies = data.ok ? (data.data.companies || []) : [];
    sel.innerHTML = companies.length
      ? companies.map((c) => `<option value="${esc(c.id)}">${esc(c.name)}</option>`).join('')
      : `<option value="">No active companies in this account</option>`;
  } catch {
    sel.innerHTML = `<option value="">Could not load companies</option>`;
  }
}

async function callrailSave(key, { refresh } = {}) {
  const btn = document.getElementById('intg-r-continue');
  const accountId = document.getElementById('intg-r-account')?.value || '';
  const companyId = document.getElementById('intg-r-company')?.value || '';
  if (!companyId) { rStatus('Pick the company this workspace tracks.'); return; }
  if (btn) btn.disabled = true;
  rStatus('Connecting…');
  try {
    const res = await guardedFetch(`${apiBase()}/m5t/v5/integrations/callrail`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ api_key: key, account_id: accountId, company_id: companyId }),
    });
    const data = await res.json();
    if (!data.ok) throw new Error(data.error || 'connect failed');
    toast(data.data?.webhook_state === 'active'
      ? 'CallRail connected. Call sync is on.'
      : 'CallRail connected. Call sync setup needs attention; see below.', 'success');
    refresh?.();
  } catch (e) {
    rStatus('Could not connect. Check the key and try again.');
    if (btn) btn.disabled = false;
  }
}

export async function syncCallrail({ refresh } = {}) {
  if (busyLock.busy) return;
  busyLock.busy = true;
  const btn = document.getElementById(PROVIDERS.callrail.card.ids.sync);
  if (btn) btn.disabled = true;
  try {
    const res = await guardedFetch(`${apiBase()}/m5t/v5/integrations/callrail/sync?days=7`, { method: 'POST' });
    const data = await res.json();
    if (!data.ok) throw new Error(data.error || 'sync failed');
    const c = data.data?.connections?.[0] || {};
    toast(c.error ? 'Sync finished with errors.' : `Sync done. ${c.processed || 0} new call${(c.processed || 0) === 1 ? '' : 's'} recorded.`, c.error ? 'warning' : 'success');
  } catch (e) {
    toast('Sync failed.', 'error');
  } finally {
    busyLock.busy = false;
    if (btn) btn.disabled = false;
    refresh?.();
  }
}

// ── the single injected stylesheet (hub + detail share it) ───────────────────
let stylesInjected = false;
export function ensureStyles() {
  if (stylesInjected) return;
  const s = document.createElement('style');
  s.id = 'm5t-intg-styles';
  s.textContent = `
    /* ── shared marks / pills / buttons / alerts ── */
    /* Brand marks sit on a neutral white chip - marks are designed for white,
       and a tinted background fought every logo's own colors. */
    .page-shell .intg-mark { position: relative; width: 62px; height: 62px; border-radius: 14px; display: inline-flex; align-items: center; justify-content: center; background: #fff; border: 1px solid var(--color-border, #D4DCE8); color: var(--color-text-muted, #4A5A78); flex: 0 0 auto; }
    .page-shell .intg-mark .material-symbols-outlined { font-size: 30px; }
    .page-shell .intg-mark--hero { width: 74px; height: 74px; border-radius: 16px; }
    .page-shell .intg-logo { width: 64%; height: 64%; object-fit: contain; display: block; }
    /* Wide marks (wordmark shapes) fill more of the chip or they turn illegible. */
    .page-shell .intg-mark--wide .intg-logo { width: 82%; height: 82%; }
    .page-shell .intg-state { display: inline-flex; align-items: center; gap: 6px; font-size: 0.8rem; font-weight: 700; letter-spacing: 0.02em; padding: 6px 14px; border-radius: 999px; background: var(--color-surface-2, #EAF0F7); color: var(--color-text-muted, #4A5A78); white-space: nowrap; }
    /* Connected/broken are SOLID fills - status is the loudest thing on the
       card by design. The -text tokens are the fills (dark enough for white
       text); the -soft versions failed to stand out. */
    .page-shell .intg-state.on { background: var(--color-success-text, #006B54); color: #fff; }
    .page-shell .intg-state.warn { background: var(--color-warning-text, #92400E); color: #fff; }
    .page-shell .intg-state .material-symbols-outlined { font-size: 16px; }
    .page-shell .intg-btn { appearance: none; border: 1px solid var(--color-border, #D4DCE8); background: var(--color-bg, #fff); cursor: pointer; font: inherit; font-size: 0.8rem; font-weight: 600; color: var(--color-accent, #0072BA); padding: 7px 13px; border-radius: 10px; display: inline-flex; align-items: center; gap: 5px; white-space: nowrap; }
    .page-shell .intg-btn:hover { background: var(--color-accent-soft, #EEF4FF); }
    .page-shell .intg-btn[disabled] { opacity: 0.55; cursor: default; }
    .page-shell .intg-btn.primary { background: var(--color-primary-600, #0072BA); border-color: var(--color-primary-600, #0072BA); color: #fff; }
    .page-shell .intg-btn.primary:hover { background: var(--color-primary-700, #00558D); }
    .page-shell .intg-btn.danger { color: var(--color-danger, #DD4124); }
    .page-shell .intg-btn .material-symbols-outlined { font-size: 16px; }
    .page-shell .intg-alert { display: flex; align-items: center; gap: 8px; margin-top: 14px; padding: 10px 14px; border-radius: 10px; font-size: 0.82rem; background: var(--color-surface, #F4F7FB); border: 1px solid var(--color-border, #D4DCE8); color: var(--color-text-muted, #4A5A78); max-width: 640px; }
    .page-shell .intg-alert.error { background: var(--color-danger-soft, #FDF1EE); border-color: var(--color-danger, #DD4124); color: var(--color-danger, #DD4124); }
    .page-shell .intg-alert .material-symbols-outlined { font-size: 17px; flex: 0 0 auto; }

    /* ── hub tiles ── */
    .page-shell .intg-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(min(350px, 100%), 1fr)); gap: 20px; }
    /* Card chrome on .intg-tile (coming-soon tiles are <div>s, not anchors);
       interactive affordances stay qualified to a.intg-tile. */
    .page-shell .intg-tile { display: flex; flex-direction: column; gap: 14px; padding: 24px; text-decoration: none; color: inherit; background: var(--color-bg, #fff); border: 1px solid var(--color-border, #D4DCE8); border-radius: var(--radius-md, 12px); box-shadow: var(--shadow-sm, 0 1px 2px rgba(11,18,32,0.06)); }
    .page-shell a.intg-tile { transition: box-shadow .15s ease, border-color .15s ease, transform .15s ease; }
    .page-shell a.intg-tile:hover { border-color: var(--color-accent-muted, #7BB7FF); box-shadow: var(--shadow-md, 0 8px 24px rgba(11,18,32,0.10)); transform: translateY(-2px); }
    .page-shell a.intg-tile:focus-visible { outline: none; box-shadow: var(--focus-ring, 0 0 0 3px rgba(0,114,186,0.30)); }
    .page-shell .intg-tile__head { display: flex; align-items: center; gap: 14px; }
    .page-shell .intg-tile__titles { min-width: 0; }
    .page-shell .intg-tile__name { font-weight: 700; font-size: 1.08rem; color: var(--color-text, #0B1220); }
    .page-shell .intg-tile__sub { font-size: 0.84rem; color: var(--color-text-subtle, #6C7C9A); margin-top: 2px; }
    .page-shell .intg-tile__desc { font-size: 0.92rem; color: var(--color-text-muted, #4A5A78); line-height: 1.55; margin: 0; flex: 1 1 auto; }
    .page-shell .intg-tile__foot { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 8px; }
    .page-shell .intg-tile__foot a { color: var(--color-accent, #0072BA); font-weight: 600; }
    .page-shell .intg-tile__go { color: var(--color-text-subtle, #6C7C9A); transition: color .12s ease, transform .12s ease; }
    .page-shell .intg-section { margin-bottom: 38px; }
    .page-shell .intg-section__title { font-size: 1.2rem; font-weight: 700; color: var(--color-text, #0B1220); margin: 0 0 3px; }
    .page-shell .intg-section__intro { font-size: 0.92rem; color: var(--color-text-subtle, #6C7C9A); margin: 0 0 15px; }
    .page-shell a.intg-tile:hover .intg-tile__go { color: var(--color-accent, #0072BA); transform: translateX(2px); }
    /* Coming-soon: inert, with only the logo mark dimmed. No tile-level
       opacity (it dropped the text and pill below readable contrast). */
    .page-shell .intg-tile--soon { pointer-events: none; }
    .page-shell .intg-tile--soon .intg-mark { filter: grayscale(1); opacity: 0.55; }
    .page-shell .intg-tile--soon .intg-tile__name, .page-shell .intg-tile--soon .intg-tile__sub { color: var(--color-text-muted, #4A5A78); }
    .page-shell .intg-tile--soon .intg-state { background: var(--color-surface, #F4F7FB); border: 1px solid var(--color-border, #D4DCE8); color: var(--color-text-muted, #4A5A78); }
    .page-shell .intg-grid__soon-label { grid-column: 1 / -1; margin: 8px 0 -6px; font-size: 0.72rem; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: var(--color-text-subtle, #6C7C9A); }

    /* ── detail page ── */
    .page-shell a.intg-back { display: inline-flex; align-items: center; gap: 4px; font-size: 0.8rem; font-weight: 600; color: var(--color-text-muted, #4A5A78); text-decoration: none; margin-bottom: 14px; }
    .page-shell a.intg-back:hover { color: var(--color-accent, #0072BA); }
    .page-shell .intg-hero { display: grid; grid-template-columns: auto 1fr auto; gap: 16px; align-items: center; }
    .page-shell .intg-hero__meta { min-width: 0; }
    .page-shell .intg-hero__name-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
    .page-shell .intg-hero__name { font-family: var(--font-display, inherit); font-weight: 700; font-size: 1.25rem; color: var(--color-text, #0B1220); }
    .page-shell .intg-hero__sub { font-size: 0.84rem; color: var(--color-text-muted, #4A5A78); margin-top: 3px; }
    .page-shell .intg-actions { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; justify-content: flex-end; }
    .page-shell .intg-desc { font-size: 0.86rem; color: var(--color-text-muted, #4A5A78); margin: 14px 0 0; max-width: 640px; line-height: 1.55; }
    .page-shell .intg-desc b { color: var(--color-text, #0B1220); }
    .page-shell .intg-steps { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 0; counter-reset: none; }
    .page-shell .intg-step { display: flex; gap: 12px; padding: 10px 0; }
    .page-shell .intg-step__num { flex: 0 0 auto; width: 26px; height: 26px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: 0.74rem; font-weight: 700; background: var(--color-surface-2, #EAF0F7); color: var(--color-text-muted, #4A5A78); }
    .page-shell .intg-step--done .intg-step__num { background: var(--color-success-soft, #E3F4EF); color: var(--color-success, #009473); }
    .page-shell .intg-step--done .intg-step__num .material-symbols-outlined { font-size: 15px; }
    .page-shell .intg-step--current .intg-step__num { background: var(--color-accent, #0072BA); color: #fff; }
    .page-shell .intg-step__text { font-size: 0.85rem; color: var(--color-text-muted, #4A5A78); line-height: 1.55; padding-top: 3px; }
    .page-shell .intg-step--current .intg-step__text { color: var(--color-text, #0B1220); }
    .page-shell .intg-kv { display: grid; grid-template-columns: 150px 1fr; gap: 8px 16px; font-size: 0.84rem; }
    .page-shell .intg-kv dt { color: var(--color-text-subtle, #6C7C9A); font-weight: 600; }
    .page-shell .intg-kv dd { margin: 0; color: var(--color-text, #0B1220); overflow-wrap: anywhere; }
    .page-shell .intg-ev { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 9px 0; border-bottom: 1px solid var(--color-surface-2, #EAF0F7); font-size: 0.85rem; }
    /* Restated because the author display rule above beats the UA's [hidden] rule,
       and core/listExpand.js hides capped rows with setAttribute('hidden'). Without
       this the cap is a no-op: the control reads "Showing 25 of N" above N visible
       rows. Same failure deliberate-fixed once already at deliverable-fill-route.js. */
    .page-shell .intg-ev[hidden] { display: none; }
    .page-shell .intg-ev:last-child { border-bottom: 0; }
    .page-shell .intg-ev__name { font-weight: 600; color: var(--color-text, #0B1220); }
    .page-shell .intg-ev__meta { color: var(--color-text-subtle, #6C7C9A); font-size: 0.76rem; }
    .page-shell .intg-ca-row { display: flex; align-items: center; gap: 10px; padding: 8px 0; border-bottom: 1px solid var(--color-surface-2, #EAF0F7); font-size: 0.85rem; cursor: pointer; }
    .page-shell .intg-ca-row:last-child { border-bottom: 0; }
    /* Restated because the author display rule above beats the UA's [hidden] rule,
       and core/listExpand.js hides capped rows with setAttribute('hidden'). Without
       this the cap is a no-op: the control reads "Showing 25 of N" above N visible
       rows. Same failure deliberate-fixed once already at deliverable-fill-route.js. */
    .page-shell .intg-ca-row[hidden] { display: none; }
    .page-shell .intg-ca-row input { accent-color: var(--color-accent, #0072BA); }
    .page-shell .intg-ca-name { font-weight: 600; color: var(--color-text, #0B1220); }
    .page-shell .intg-ca-cat { font-size: 0.68rem; font-weight: 700; letter-spacing: 0.03em; padding: 2px 8px; border-radius: 999px; background: var(--color-surface-2, #EAF0F7); color: var(--color-text-muted, #4A5A78); }

    /* ── shared sub-panels (picker, CallRail form, walkthrough) ── */
    .page-shell .intg-picker { margin-top: 16px; border: 1px solid var(--color-border, #D4DCE8); border-radius: 12px; overflow: hidden; max-width: 640px; }
    .page-shell .intg-picker__head { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 10px 16px; background: var(--color-surface, #F4F7FB); border-bottom: 1px solid var(--color-border, #D4DCE8); font-size: 0.8rem; font-weight: 700; color: var(--color-text-muted, #4A5A78); letter-spacing: 0.02em; }
    .page-shell .intg-picker__list { max-height: 300px; overflow-y: auto; }
    .page-shell .intg-acct { display: grid; grid-template-columns: auto 1fr auto; gap: 12px; align-items: center; padding: 10px 16px; border-bottom: 1px solid var(--color-surface-2, #EAF0F7); cursor: pointer; }
    .page-shell .intg-acct:last-child { border-bottom: 0; }
    .page-shell .intg-acct:hover { background: var(--color-surface, #F4F7FB); }
    .page-shell .intg-acct.disabled { opacity: 0.55; cursor: default; }
    .page-shell .intg-acct input { width: 15px; height: 15px; accent-color: var(--color-primary-600, #0072BA); }
    .page-shell .intg-acct__name { font-size: 0.86rem; font-weight: 600; color: var(--color-text, #0B1220); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .page-shell .intg-acct__id { font-size: 0.74rem; color: var(--color-text-subtle, #6C7C9A); font-family: ui-monospace, monospace; margin-top: 1px; }
    .page-shell .intg-acct__badge { font-size: 0.68rem; font-weight: 700; padding: 2px 8px; border-radius: 999px; background: var(--color-surface-2, #EAF0F7); color: var(--color-text-subtle, #6C7C9A); white-space: nowrap; }
    .page-shell .intg-picker__foot { display: flex; justify-content: flex-end; gap: 8px; padding: 10px 16px; background: var(--color-surface, #F4F7FB); border-top: 1px solid var(--color-border, #D4DCE8); }
    .page-shell .intg-picker__empty { padding: 18px 16px; font-size: 0.84rem; color: var(--color-text-subtle, #6C7C9A); }
    .page-shell .intg-form { display: flex; flex-direction: column; gap: 10px; padding: 14px 16px; }
    .page-shell .intg-form label { font-size: 0.74rem; font-weight: 700; letter-spacing: 0.03em; color: var(--color-text-muted, #4A5A78); }
    .page-shell .intg-input, .page-shell .intg-select { font: inherit; font-size: 0.84rem; padding: 8px 10px; border-radius: 10px; border: 1px solid var(--color-border, #D4DCE8); background: var(--color-bg, #fff); color: var(--color-text, #0B1220); width: 100%; box-sizing: border-box; }
    .page-shell .intg-input:focus, .page-shell .intg-select:focus { outline: none; border-color: var(--color-primary-600, #0072BA); }
    .page-shell .intg-hint { font-size: 0.75rem; color: var(--color-text-subtle, #6C7C9A); margin: 0; }
    .page-shell .intg-walkthrough { margin-top: 14px; max-width: 640px; }
    .page-shell .intg-walkthrough summary { cursor: pointer; list-style: none; font-size: 0.8rem; font-weight: 600; color: var(--color-accent, #0072BA); display: inline-flex; align-items: center; gap: 5px; }
    .page-shell .intg-walkthrough summary::-webkit-details-marker { display: none; }
    .page-shell .intg-walkthrough summary::before { content: 'expand_more'; font-family: 'Material Symbols Outlined'; font-size: 18px; transition: transform .15s ease; }
    .page-shell .intg-walkthrough[open] summary::before { transform: rotate(180deg); }
    .page-shell .intg-walkthrough summary:hover { color: var(--color-accent-hover, #00558D); }
    .page-shell .intg-walkthrough__body { margin-top: 10px; padding: 14px 16px; border-radius: 10px; background: var(--color-surface, #F4F7FB); border: 1px solid var(--color-border, #D4DCE8); }
    .page-shell .intg-walkthrough__body p { margin: 0 0 10px; font-size: 0.84rem; color: var(--color-text, #0B1220); line-height: 1.55; }
    .page-shell .intg-walkthrough__body ol { margin: 0; padding-left: 20px; display: flex; flex-direction: column; gap: 7px; }
    .page-shell .intg-walkthrough__body li { font-size: 0.82rem; color: var(--color-text-muted, #4A5A78); line-height: 1.5; }
    .page-shell .intg-card { margin-bottom: 20px; }

    @media (max-width: 720px) {
      .page-shell .intg-hero { grid-template-columns: auto 1fr; }
      .page-shell .intg-actions { grid-column: 1 / -1; justify-content: flex-start; }
      .page-shell .intg-kv { grid-template-columns: 1fr; gap: 2px 0; }
      .page-shell .intg-kv dd { margin-bottom: 8px; }
    }

    html.ff-no-animations .page-shell a.intg-tile,
    html.ff-no-animations .page-shell .intg-tile__go,
    html.ff-no-animations .page-shell .intg-walkthrough summary::before { transition: none; }
    @media (prefers-reduced-motion: reduce) {
      .page-shell a.intg-tile, .page-shell .intg-tile__go,
      .page-shell .intg-walkthrough summary::before { transition: none; transform: none; }
    }
  `;
  document.head.appendChild(s);
  stylesInjected = true;
}
